import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { InvoiceService } from '../../service/data/invoice.service';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Invoice } from '../../model/invoice';
import { GetInvoiceDeliveryMethodIcon, InvoiceDeliveryMethod, InvoiceDeliveryMethodToString } from '../../model/const/enum';
import { Subscription } from 'rxjs';
import { ProfileService } from '../../../common/service/data/profile.service';

@Component({
  selector: 'app-deliver-invoice',
  templateUrl: './deliver-invoice.component.html',
  styleUrls: ['./deliver-invoice.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DeliverInvoiceComponent implements OnInit, OnDestroy {
  constructor(
    private _dataService: InvoiceService,
    private _modalService: ActionWindowService,
    cf: FormControlExFactory,
    private _profileService: ProfileService,
    fb: FormBuilder
  ) {
    this.form = fb.group({
      deliveryMethod: cf.createMandatory('INVOICE.DELIVERY_METHOD', InvoiceDeliveryMethod.Mail),
      invoiceRecipientEmail: cf.create('COMMON.EMAIL_ADDRESS', null),
      id: cf.createHidden(''),
    });
  }

  private sub: Subscription;
  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  private _dataContext: {
    invoices?: Invoice[];
    emailCount?: number;
    mailCount?: number;
    b2bCount?: number;
    selection: {
      filter: any;
      includedIds: string[];
      excludedIds: string[];
      affectedItemsCount: number;
    };
  };
  get dataContext(): {
    invoices?: Invoice[];
    emailCount?: number;
    mailCount?: number;
    b2bCount?: number;
    selection: {
      filter: any;
      includedIds: string[];
      excludedIds: string[];
      affectedItemsCount: number;
    };
  } {
    return this._dataContext;
  }
  set dataContext(value: {
    invoices?: Invoice[];
    emailCount?: number;
    mailCount?: number;
    b2bCount?: number;
    selection: {
      filter: any;
      includedIds: string[];
      excludedIds: string[];
      affectedItemsCount: number;
    };
  }) {
    this._dataContext = value;
    if (value.invoices instanceof Array) {
      if (this.dataContext.invoices && this.dataContext.invoices.length == 1) {
        this.invoice = this.dataContext.invoices[0];
        this.form.controls['id'].setValue(this.invoice.id);
        if (this.invoice.deliveryMethod == InvoiceDeliveryMethod.Manual)
          this.form.controls['deliveryMethod'].setValue(InvoiceDeliveryMethod.Mail);
        else this.form.controls['deliveryMethod'].setValue(this.invoice.deliveryMethod);
        this.form.controls['invoiceRecipientEmail'].setValue(this.invoice.invoiceRecipientEmail);
      }
    }
  }
  readonly form: FormGroup;
  deliveryMethods = [
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Mail), value: InvoiceDeliveryMethod.Mail, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Mail) },
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EmailWithPDF), value: InvoiceDeliveryMethod.EmailWithPDF, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EmailWithPDF) },
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EmailWithoutPDF), value: InvoiceDeliveryMethod.EmailWithoutPDF, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EmailWithoutPDF) },
    { name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.EInvoiceB2B), value: InvoiceDeliveryMethod.EInvoiceB2B, icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.EInvoiceB2B) },
  ];

  invoice: Invoice;

  get mailDeliveryAddress(): string {
    if (this.invoice.debtor.address)
      return (
        this.invoice.debtor.address.name +
        ', ' +
        this.invoice.debtor.address.street +
        ', ' +
        this.invoice.debtor.address.postalCode +
        ' ' +
        this.invoice.debtor.address.city
      );
    else return '';
  }

  get isDeliveryMethodEmail(): boolean {
    if (this.form.controls['deliveryMethod'].value)
      return (
        this.form.controls['deliveryMethod'].value === InvoiceDeliveryMethod.EmailWithPDF ||
        this.form.controls['deliveryMethod'].value === InvoiceDeliveryMethod.EmailWithoutPDF
      );
    else return false;
  }

  get isDeliveryMethodMail(): boolean {
    if (this.form.controls['deliveryMethod'].value) return this.form.controls['deliveryMethod'].value === InvoiceDeliveryMethod.Mail;
    else return false;
  }

  get viaEmail(): number {
    if (this.dataContext.invoices) {
      return (
        this.dataContext.invoices.filter(x => x.deliveryMethod === InvoiceDeliveryMethod.EmailWithPDF).length +
        this.dataContext.invoices.filter(x => x.deliveryMethod === InvoiceDeliveryMethod.EmailWithoutPDF).length
      );
    }
    return this.dataContext.emailCount;
  }
  get viaMail(): number {
    if (this.dataContext.invoices) {
      return this.dataContext.invoices.filter(x => x.deliveryMethod === InvoiceDeliveryMethod.Mail).length;
    }
    return this.dataContext.mailCount;
  }
  get viaEInvoiceB2B(): number {
    if (this.dataContext.invoices) {
      return this.dataContext.invoices.filter(x => x.deliveryMethod === InvoiceDeliveryMethod.EInvoiceB2B).length;
    }
    return this.dataContext.b2bCount;
  }

  cancel() {
    this._modalService.close();
  }

  ngOnInit() {
    this.updateDeliveryMethods();
  }

  updateDeliveryMethods(): void {
    this._profileService.getConfig().subscribe(res => {
      if (res.useKivra) {
        this.deliveryMethods.push({
          name: InvoiceDeliveryMethodToString(InvoiceDeliveryMethod.Kivra),
          value: InvoiceDeliveryMethod.Kivra,
          icon: GetInvoiceDeliveryMethodIcon(InvoiceDeliveryMethod.Kivra),
        });
      }
    });
  }

  onSubmit() {
    if (this.form.invalid) return;

    this.form.disable();

    if (this.sub) {
      this.sub.unsubscribe();
    }

    if (this.invoice) {
      this.sub = this._dataService.send(this.form.value).subscribe(
        res => {
          this._modalService.complete(res);
          this._modalService.close();
        },
        error => this.form.enable()
      );
      return;
    }
    if (this.dataContext.invoices) {
      this.sub = this._dataService.sendMany(this.dataContext.invoices.map(x => x.id)).subscribe(
        res => {
          this._modalService.complete(res);
          this._modalService.close();
        },
        error => this.form.enable()
      );
      return;
    }
    this.sub = this._dataService.sendByFilter(this.dataContext.selection).subscribe(
      res => {
        this._modalService.complete(res);
        this._modalService.close();
      },
      error => this.form.enable()
    );
  }
}
